import { useIsMobile } from '@src/hooks/useIsMobile';
import { EButtonIntents, ETypographyIntents } from '@src/typings';
import { Divider } from 'antd';
import clsx from 'clsx';
import { HiDocument, HiFolderOpen } from 'react-icons/hi2';
import { Button } from '../Button';
import { UploadFile } from '../FileUpload';
import { Tabs } from '../Tab';
import { withTheme } from '../ThemeProvider/withTheme';
import { Typography } from '../Typography';
import { StyledModal } from './styles';
import { SelectUploadDocumentsProps } from './types';

const SelectUploadDocumentsComponent = ({
    title = 'Select / Upload Authorized Documents',
    files,
    className,
    uploadCallback,
    onUploadRemove,
    onRecentlyFileClick,
    onSubmit,
    onClose,
    open = false,
    theme,
}: SelectUploadDocumentsProps) => {
    const isMobile = useIsMobile();

    return (
        <StyledModal
            open={open}
            title={
                <div className="flex text-primaryBlack justify-center items-center">
                    <HiFolderOpen
                        className="min-w-6 min-h-6 mr-2"
                        color={theme?.colors.secondary.main.icon}
                    />
                    <Typography
                        intent={
                            isMobile
                                ? ETypographyIntents.H2Phone
                                : ETypographyIntents.H2
                        }
                    >
                        {title}
                    </Typography>
                </div>
            }
            className={clsx('rounded-xl w-[400px] md:w-[500px]', className)}
            onCancel={onClose}
        >
            <Tabs
                items={[
                    {
                        key: '1',
                        label: 'Upload New',
                        children: (
                            <div className="flex flex-col">
                                <div className="p-4">
                                    <UploadFile
                                        label="Letter of intent (LOI)"
                                        required
                                        title={
                                            <Typography
                                                intent={
                                                    isMobile
                                                        ? ETypographyIntents.H2Phone
                                                        : ETypographyIntents.H2
                                                }
                                            >
                                                Drag Your File Here or
                                            </Typography>
                                        }
                                        uploadBtn="Select a File"
                                        callBack={uploadCallback}
                                        onRemove={onUploadRemove}
                                        accept=".xls, .xlsx, .csv, .txt"
                                    />
                                </div>
                                <Divider />
                                <div className="flex p-4">
                                    <Button
                                        size={isMobile ? 'sm' : 'md'}
                                        className="w-full mr-4"
                                        intent={EButtonIntents.LightLinear}
                                        onClick={onClose}
                                    >
                                        Close
                                    </Button>
                                    <Button
                                        size={isMobile ? 'sm' : 'md'}
                                        className="w-full"
                                        intent={EButtonIntents.Filled}
                                        onClick={onSubmit}
                                    >
                                        Submit
                                    </Button>
                                </div>
                            </div>
                        ),
                    },
                    {
                        key: '2',
                        label: 'Recently Uploaded',
                        children: (
                            <div className="flex flex-col">
                                {files &&
                                    files.map((file) => (
                                        <div
                                            key={file.id}
                                            className="flex justify-center items-center px-6 py-2 first:pt-1 border-b cursor-pointer hover:bg-selectUploadDocumentsFileHoverBg"
                                            style={{
                                                borderColor:
                                                    theme?.colors.secondary
                                                        .light.divider,
                                                backgroundColor:
                                                    theme?.colors.neutral.light
                                                        .contentBg,
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() =>
                                                onRecentlyFileClick(file)
                                            }
                                            onKeyDown={(e) => {
                                                if (
                                                    e.key === 'Enter' ||
                                                    e.key === ' '
                                                ) {
                                                    e.preventDefault();
                                                    onRecentlyFileClick(file);
                                                }
                                            }}
                                        >
                                            <div
                                                className="p-2 rounded-lg mr-2"
                                                style={{
                                                    backgroundColor:
                                                        theme?.colors.secondary
                                                            .light.surfaceBg,
                                                }}
                                            >
                                                <HiDocument
                                                    className="min-w-4 min-h-4"
                                                    color={
                                                        theme?.colors.secondary
                                                            .main.icon
                                                    }
                                                />
                                            </div>
                                            <Typography
                                                className="grow"
                                                intent={ETypographyIntents.H3}
                                                style={{
                                                    color: theme?.colors
                                                        .secondary.main.text,
                                                }}
                                            >
                                                {file.name}
                                            </Typography>
                                            <Typography
                                                className="w-auto text-nowrap"
                                                intent={
                                                    ETypographyIntents.Regular
                                                }
                                                style={{
                                                    color: theme?.colors
                                                        .secondary.main.text,
                                                }}
                                            >
                                                {file.size}
                                            </Typography>
                                        </div>
                                    ))}
                                <div className="flex p-4">
                                    <Button
                                        size={isMobile ? 'sm' : 'md'}
                                        className="w-full"
                                        intent={EButtonIntents.LightLinear}
                                        onClick={onClose}
                                    >
                                        Close
                                    </Button>
                                </div>
                            </div>
                        ),
                    },
                ]}
            />
        </StyledModal>
    );
};

export const SelectUploadDocuments = withTheme(SelectUploadDocumentsComponent);
